<template>
  <div
    class="app-card-glass backdrop-blur-sm rounded-t-lg flex items-center justify-between px-4 py-3  shadow-xs relative z-10 cursor-pointer transition-colors hover:bg-[var(--p-surface-50)] dark:hover:bg-[var(--p-surface-800)]"
    role="button"
    tabindex="0"
    @click="$emit('open-contact-info')"
    @keydown.enter.prevent="$emit('open-contact-info')"
    @keydown.space.prevent="$emit('open-contact-info')"
  >
    <div class="flex min-w-0 items-center gap-2">
      <button
        v-if="showBackButton"
        type="button"
        class="cursor-pointer flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-[var(--p-primary-color)] transition-colors hover:bg-[var(--p-primary-50)] active:scale-95 md:hidden dark:text-[var(--p-primary-100)] dark:hover:bg-[var(--p-primary-900)]/40"
        aria-label="Volver a la lista de chats"
        @click.stop="$emit('back-to-list')"
      >
        <ArrowLeft :size="22" :stroke-width="2.25" />
      </button>

      <AppAvatar
        :label="contactInitials"
        :shape="'circle'"
        size="large"
        class="!h-12 !w-12 !rounded-full flex-shrink-0 font-bold text-sm! shadow-sm shadow-[var(--p-primary-color)] dark:bg-[var(--p-primary-color)]/20!"
      />
      <!-- IZQUIERDA -->
      <div class="flex min-w-0 items-center gap-3">
        <div class="flex min-w-0 flex-col">
          <h3
            class="montserrat text-sm text-[var(--p-surface-800)] dark:text-[var(--p-surface-0)] font-semibold  truncate"
          >
            {{ displayName }}
            <span
              v-if="usernameLabel"
              class="font-normal text-xs text-[var(--p-surface-500)] dark:text-[var(--p-primary-100)]/80"
            >
              {{ usernameLabel }}
            </span>
          </h3>
          <div class="flex min-w-0 items-center gap-1">
            <IconCampaign
              width="12"
              height="12"
              class="flex-shrink-0 text dark:text-[var(--p-surface-300)] dark:text-[var(--p-surface-0)]/90"
            />
            <p
              class="montserrat text-xs text-[var(--p-surface-800)] dark:text-[var(--p-surface-0)]/90 truncate"
            >
              {{ campaignName || '-' }}
            </p>
            <span
              v-if="isTyping"
              class="ml-1 flex-shrink-0 animate-pulse text-[11px] font-semibold text-emerald-600 dark:text-emerald-400"
            >
              {{ $t('whatsapp.chat_header.typing') }}
            </span>
            <span
              v-if="isWebchat"
              class="ml-1 inline-flex flex-shrink-0 items-center gap-1 rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-semibold text-blue-600 dark:bg-blue-900/40 dark:text-blue-300"
            >
              <MessageCircle :size="10" :stroke-width="2.5" />
              {{ $t('whatsapp.chat_header.webchat_badge') }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <div class="hidden flex-shrink-0 gap-4 lg:flex">
      <!-- Info del chat -->
      <div class="flex text-sm gap-2">
        <div
          class="montserrat font-semibold flex text-[11px] flex-col text-end text-[var(--p-surface-600)] dark:text-[var(--p-surface-0)]/80 "
        >
          <span>{{ $t('whatsapp.chat_header.start') }}:</span>
          <span>{{ $t('whatsapp.chat_header.last_message') }}:</span>
          <span>{{ $t('whatsapp.chat_header.messages') }}:</span>
        </div>

        <div
          class="montserrat font-semibold flex flex-col leading text-[11px] text-end text-[var(--p-surface-600)] dark:text-[var(--p-surface-0)]/80 "
        >
          <span>
            {{ startDate }}
          </span>
          <span>
            {{ lastMessageDate }}
          </span>
          <span>
            {{ messageCount }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { ArrowLeft, MessageCircle } from 'lucide-vue-next'
import AppAvatar from '@/components/atoms/avatar/AppAvatar.vue'
import IconCampaign from '@/assets/svg/campaign_icon.svg?component'
import { WEBCHAT_CHANNEL_ID } from '../../services/chatChannel'
import { formatUsername } from '../../services/contactIdentity'

interface Props {
  contactName: string
  contactProfileName?: string | null
  contactUsername?: string | null
  campaignName?: string
  channelId?: string
  isTyping?: boolean

  startDate: string
  lastMessageDate: string
  messageCount: number

  showBackButton?: boolean
}

const getInitials = (name: string) => {
  const words = name.trim().split(/\s+/).filter(Boolean)

  if (!words.length) return '?'

  return words
    .slice(0, 2)
    .map((word) => word.charAt(0).toUpperCase())
    .join('')
}

const props = defineProps<Props>()
const displayName = computed(() => {
  return props.contactName?.trim() || props.contactProfileName?.trim() || ''
})

// El username va junto al nombre de perfil, salvo que ya sea el nombre (el
// cliente no puso ninguno).
const usernameLabel = computed(() => {
  const username = formatUsername(props.contactUsername)
  return username !== displayName.value ? username : ''
})

const contactInitials = computed(() => getInitials(displayName.value))

const isWebchat = computed(() => props.channelId === WEBCHAT_CHANNEL_ID)

defineEmits<{
  'back-to-list': []
  'open-contact-info': []
}>()
</script>
